import React, { createContext, useContext, useState, useEffect } from 'react';

export const translations = {
  en: {
    // Sidebar
    'sidebar.dashboard': 'Dashboard',
    'sidebar.properties': 'Properties',
    'sidebar.bookings': 'Bookings',
    'sidebar.guests': 'Guests',
    'sidebar.income': 'Income',
    'sidebar.logout': 'Logout',
    'sidebar.subtitle': 'Property Manager',
    // Source labels (shared)
    'sources.direct': 'Direct',
    'sources.manual': 'Manual',
    'sources.airbnb': 'Airbnb',
    'sources.booking': 'Booking.com',
    'sources.whatsapp_link': 'WhatsApp Link',
    'sources.whatsapp': 'WhatsApp',
    // Status labels
    'status.all': 'All',
    'status.confirmed': 'Confirmed',
    'status.cancelled': 'Cancelled',
    'status.completed': 'Completed',
    // Dashboard
    'dashboard.greeting.morning': 'Good morning',
    'dashboard.greeting.afternoon': 'Good afternoon',
    'dashboard.greeting.evening': 'Good evening',
    'dashboard.subtitle': "Here's what's happening with your properties today.",
    'dashboard.stat.properties': 'Total Properties',
    'dashboard.stat.guests': 'Current Guests',
    'dashboard.stat.checkouts': "Today's Checkouts",
    'dashboard.stat.income': 'Income This Month',
    'dashboard.checkoutsToday': "Today's Checkouts",
    'dashboard.checkoutsTomorrow': "Tomorrow's Checkouts",
    'dashboard.noCheckoutsToday': 'No checkouts today 🎉',
    'dashboard.noCheckoutsTomorrow': 'No checkouts tomorrow',
    'dashboard.recentBookings': 'Recent Bookings',
    'dashboard.viewAll': 'View all',
    'dashboard.noBookings': 'No bookings yet',
    'dashboard.noBookingsDesc': 'Add your first property and start accepting bookings',
    'dashboard.today': 'Today',
    'dashboard.tomorrow': 'Tomorrow',
    // Table headers
    'table.guest': 'Guest',
    'table.property': 'Property',
    'table.checkIn': 'Check In',
    'table.checkOut': 'Check Out',
    'table.source': 'Source',
    'table.status': 'Status',
    'table.guests': 'Guests',
    'table.amount': 'Amount',
    'table.fullName': 'Full Name',
    'table.nationality': 'Nationality',
    'table.idType': 'ID Type',
    'table.idNumber': 'ID Number',
    'table.phone': 'Phone',
    'table.email': 'Email',
    'table.bookings': 'Bookings',
    'table.city': 'City',
    'table.bookingsThisMonth': 'Bookings This Month',
    'table.thisMonth': 'This Month',
    'table.allTime': 'All Time',
    // Common
    'action.cancel': 'Cancel',
    'action.save': 'Save Changes',
    'action.addProperty': 'Add Property',
    'action.newBooking': 'New Booking',
    // Properties
    'properties.title': 'Properties',
    'properties.count': (n) => `${n} ${n === 1 ? 'property' : 'properties'} managed`,
    'properties.addNew': 'Add New Property',
    'properties.edit': 'Edit Property',
    'properties.empty': 'No properties yet',
    'properties.emptyDesc': 'Add your first property to start managing bookings.',
    'properties.addFirst': 'Add First Property',
    'properties.name': 'Property Name *',
    'properties.namePlaceholder': 'Appartement Centre-ville',
    'properties.city': 'City *',
    'properties.cityPlaceholder': 'Casablanca',
    'properties.type': 'Type',
    'properties.address': 'Address *',
    'properties.addressPlaceholder': '123 Rue Mohammed V',
    'properties.bedrooms': 'Bedrooms',
    'properties.maxGuests': 'Max Guests',
    'properties.priceNight': 'Price/Night (MAD)',
    'properties.thisMonth': 'This month',
    'properties.activeGuests': 'Active guests',
    'properties.max': 'max',
    'properties.confirmRemove': 'Remove this property?',
    'properties.type.apartment': 'Apartment',
    'properties.type.villa': 'Villa',
    'properties.type.riad': 'Riad',
    'properties.type.studio': 'Studio',
    'properties.type.house': 'House',
    // Bookings
    'bookings.title': 'Bookings',
    'bookings.count': (n) => `${n} total bookings`,
    'bookings.searchPlaceholder': 'Search guest or property...',
    'bookings.noBookings': 'No bookings found',
    'bookings.noBookingsDesc': 'Add your first booking or adjust your filters',
    'bookings.modalTitle': 'New Booking',
    'bookings.step1': 'Booking Details',
    'bookings.step2': 'Guest Info',
    'bookings.property': 'Property *',
    'bookings.selectProperty': 'Select a property',
    'bookings.checkIn': 'Check In *',
    'bookings.checkOut': 'Check Out *',
    'bookings.numGuests': 'Guests',
    'bookings.totalPrice': 'Total Price (MAD)',
    'bookings.source': 'Source',
    'bookings.notes': 'Notes',
    'bookings.notesPlaceholder': 'Any notes...',
    'bookings.nextStep': 'Next: Guest Info →',
    'bookings.guestName': 'Guest Full Name *',
    'bookings.guestNamePlaceholder': 'Mohammed Alami',
    'bookings.phone': 'Phone',
    'bookings.email': 'Email',
    'bookings.nationality': 'Nationality',
    'bookings.nationalityDefault': 'Marocaine',
    'bookings.idType': 'ID Type *',
    'bookings.idNumber': 'ID Number *',
    'bookings.back': '← Back',
    'bookings.create': 'Create Booking',
    'bookings.editPrice': 'Edit Price',
    'bookings.setPrice': 'Set Price',
    'bookings.guestLabel': 'Guest:',
    'bookings.totalPriceLabel': 'Total Price (MAD) *',
    'bookings.currentPrice': 'Current price:',
    'bookings.confirmPayment': 'Confirm & Record Payment',
    'bookings.updatePrice': 'Update Price',
    'bookings.fillRequired': 'Fill required fields',
    'bookings.cin': 'CIN',
    'bookings.passport': 'Passport',
    'bookings.residence': 'Residence Card',
    // Guests
    'guests.title': 'Guests',
    'guests.count': (n) => `${n} total guests`,
    'guests.searchPlaceholder': 'Search by name or ID number...',
    'guests.noGuests': 'No guests found',
    'guests.noGuestsSearch': 'Try a different name or ID number.',
    'guests.noGuestsEmpty': 'Guests will appear here once bookings are created.',
    // Income
    'income.title': 'Income',
    'income.subtitle': 'Revenue overview across all properties',
    'income.thisMonth': 'This Month',
    'income.allTime': 'All Time',
    'income.bestMonth': 'Best Month',
    'income.chartTitle': 'Monthly Revenue — Last 12 Months',
    'income.noData': 'No payment data yet. Income will appear here once bookings are paid.',
    'income.byProperty': (month) => `By Property — ${month}`,
    'income.noProperties': 'No properties found',
    'income.noPropertiesDesc': 'Add a property to start tracking income.',
    // Login
    'login.title': 'Welcome back',
    'login.subtitle': 'Sign in to your Nuzlio account',
    'login.email': 'Email address',
    'login.password': 'Password',
    'login.submit': 'Sign In',
    'login.noAccount': 'No account?',
    'login.createOne': 'Create one',
    // Register
    'register.title': 'Start managing',
    'register.subtitle': 'Create your free Nuzlio account',
    'register.fullName': 'Full Name',
    'register.fullNamePlaceholder': 'Your full name',
    'register.email': 'Email',
    'register.phone': 'Phone (optional)',
    'register.password': 'Password',
    'register.passwordPlaceholder': 'Min. 8 characters',
    'register.submit': 'Create Account',
    'register.haveAccount': 'Already have an account?',
    'register.signIn': 'Sign in',
    // BookingForm (public)
    'bookingForm.badge': 'Booking Request',
    'bookingForm.step1': 'Stay Details',
    'bookingForm.step2': 'Guest Info',
    'bookingForm.checkIn': 'Check In *',
    'bookingForm.checkOut': 'Check Out *',
    'bookingForm.numGuests': 'Number of Guests *',
    'bookingForm.notes': 'Notes / Special Requests',
    'bookingForm.notesPlaceholder': 'Arrival time, special requirements...',
    'bookingForm.night': 'night',
    'bookingForm.nights': 'nights',
    'bookingForm.continue': 'Continue to Guest Info →',
    'bookingForm.fullName': 'Full Name *',
    'bookingForm.fullNamePlaceholder': 'Mohammed Alami',
    'bookingForm.phone': 'Phone',
    'bookingForm.email': 'Email',
    'bookingForm.nationality': 'Nationality',
    'bookingForm.nationalityDefault': 'Moroccan',
    'bookingForm.idType': 'ID Type *',
    'bookingForm.idNumber': 'ID Number *',
    'bookingForm.idNumberPlaceholder': 'AB123456',
    'bookingForm.cin': 'CIN',
    'bookingForm.passport': 'Passport',
    'bookingForm.residence': 'Residence Card',
    'bookingForm.summaryTitle': 'Booking Summary',
    'bookingForm.summaryProperty': 'Property',
    'bookingForm.summaryCheckin': 'Check-in',
    'bookingForm.summaryCheckout': 'Check-out',
    'bookingForm.summaryDuration': 'Duration',
    'bookingForm.summaryTotal': 'Total',
    'bookingForm.back': '← Back',
    'bookingForm.submit': 'Submit Booking Request',
    'bookingForm.errorDates': 'Please select check-in and check-out dates',
    'bookingForm.errorCheckout': 'Check-out must be after check-in',
    'bookingForm.linkTitle': 'Link Not Found',
    'bookingForm.linkDesc': 'This booking link is invalid or has expired.',
    'bookingForm.doneTitle': 'Booking Submitted!',
    'bookingForm.doneDesc': (name) => `Your request for ${name} has been received. The host will confirm shortly.`,
    'bookingForm.upToGuests': (n) => `Up to ${n} guests`,
    'bookingForm.footer': 'Powered by Nuzlio · Your data is securely handled',
    // Pricing
    'pricing.title': 'Simple, transparent pricing',
    'pricing.subtitle': 'Start free. Scale as you grow. No hidden fees, no surprises.',
    'pricing.plan.free': 'Free',
    'pricing.plan.basic': 'Basic',
    'pricing.plan.pro': 'Pro',
    'pricing.plan.agency': 'Agency',
    'pricing.mostPopular': 'Most Popular',
    'pricing.feature.1property': '1 property',
    'pricing.feature.upTo3': 'Up to 3 properties',
    'pricing.feature.upTo10': 'Up to 10 properties',
    'pricing.feature.unlimitedProperties': 'Unlimited properties',
    'pricing.feature.unlimitedBookings': 'Unlimited bookings',
    'pricing.feature.guestRecords': 'Guest records',
    'pricing.feature.incomeReports': 'Income reports',
    'pricing.feature.publicLinks': 'Public booking links',
    'pricing.feature.prioritySupport': 'Priority support',
    'pricing.btn.getStartedFree': 'Get started free',
    'pricing.btn.start': (name) => `Start ${name}`,
    'pricing.btn.currentPlan': 'Current plan',
    'pricing.btn.backToDashboard': '← Back to dashboard',
  },

  fr: {
    'sidebar.dashboard': 'Tableau de bord',
    'sidebar.properties': 'Propriétés',
    'sidebar.bookings': 'Réservations',
    'sidebar.guests': 'Clients',
    'sidebar.income': 'Revenus',
    'sidebar.logout': 'Déconnexion',
    'sidebar.subtitle': 'Gestionnaire',
    'sources.direct': 'Direct',
    'sources.manual': 'Manuel',
    'sources.airbnb': 'Airbnb',
    'sources.booking': 'Booking.com',
    'sources.whatsapp_link': 'Lien WhatsApp',
    'sources.whatsapp': 'WhatsApp',
    'status.all': 'Tous',
    'status.confirmed': 'Confirmé',
    'status.cancelled': 'Annulé',
    'status.completed': 'Terminé',
    'dashboard.greeting.morning': 'Bonjour',
    'dashboard.greeting.afternoon': 'Bon après-midi',
    'dashboard.greeting.evening': 'Bonsoir',
    'dashboard.subtitle': "Voici ce qui se passe avec vos propriétés aujourd'hui.",
    'dashboard.stat.properties': 'Total des propriétés',
    'dashboard.stat.guests': 'Clients actuels',
    'dashboard.stat.checkouts': "Départs aujourd'hui",
    'dashboard.stat.income': 'Revenus ce mois',
    'dashboard.checkoutsToday': "Départs aujourd'hui",
    'dashboard.checkoutsTomorrow': 'Départs demain',
    'dashboard.noCheckoutsToday': "Aucun départ aujourd'hui 🎉",
    'dashboard.noCheckoutsTomorrow': 'Aucun départ demain',
    'dashboard.recentBookings': 'Réservations récentes',
    'dashboard.viewAll': 'Voir tout',
    'dashboard.noBookings': 'Aucune réservation',
    'dashboard.noBookingsDesc': 'Ajoutez votre première propriété et commencez à accepter des réservations',
    'dashboard.today': "Aujourd'hui",
    'dashboard.tomorrow': 'Demain',
    'table.guest': 'Client',
    'table.property': 'Propriété',
    'table.checkIn': 'Arrivée',
    'table.checkOut': 'Départ',
    'table.source': 'Source',
    'table.status': 'Statut',
    'table.guests': 'Clients',
    'table.amount': 'Montant',
    'table.fullName': 'Nom complet',
    'table.nationality': 'Nationalité',
    'table.idType': "Type d'ID",
    'table.idNumber': "N° d'ID",
    'table.phone': 'Téléphone',
    'table.email': 'Email',
    'table.bookings': 'Réservations',
    'table.city': 'Ville',
    'table.bookingsThisMonth': 'Réservations ce mois',
    'table.thisMonth': 'Ce mois',
    'table.allTime': 'Total',
    'action.cancel': 'Annuler',
    'action.save': 'Enregistrer',
    'action.addProperty': 'Ajouter une propriété',
    'action.newBooking': 'Nouvelle réservation',
    'properties.title': 'Propriétés',
    'properties.count': (n) => `${n} ${n === 1 ? 'propriété gérée' : 'propriétés gérées'}`,
    'properties.addNew': 'Ajouter une propriété',
    'properties.edit': 'Modifier la propriété',
    'properties.empty': 'Aucune propriété',
    'properties.emptyDesc': 'Ajoutez votre première propriété pour commencer à gérer les réservations.',
    'properties.addFirst': 'Ajouter la première propriété',
    'properties.name': 'Nom de la propriété *',
    'properties.namePlaceholder': 'Appartement Centre-ville',
    'properties.city': 'Ville *',
    'properties.cityPlaceholder': 'Casablanca',
    'properties.type': 'Type',
    'properties.address': 'Adresse *',
    'properties.addressPlaceholder': '123 Rue Mohammed V',
    'properties.bedrooms': 'Chambres',
    'properties.maxGuests': 'Clients max',
    'properties.priceNight': 'Prix/Nuit (MAD)',
    'properties.thisMonth': 'Ce mois',
    'properties.activeGuests': 'Clients actifs',
    'properties.max': 'max',
    'properties.confirmRemove': 'Supprimer cette propriété?',
    'properties.type.apartment': 'Appartement',
    'properties.type.villa': 'Villa',
    'properties.type.riad': 'Riad',
    'properties.type.studio': 'Studio',
    'properties.type.house': 'Maison',
    'bookings.title': 'Réservations',
    'bookings.count': (n) => `${n} réservations au total`,
    'bookings.searchPlaceholder': 'Rechercher client ou propriété...',
    'bookings.noBookings': 'Aucune réservation',
    'bookings.noBookingsDesc': 'Ajoutez votre première réservation ou ajustez les filtres',
    'bookings.modalTitle': 'Nouvelle réservation',
    'bookings.step1': 'Détails',
    'bookings.step2': 'Info client',
    'bookings.property': 'Propriété *',
    'bookings.selectProperty': 'Sélectionner une propriété',
    'bookings.checkIn': 'Arrivée *',
    'bookings.checkOut': 'Départ *',
    'bookings.numGuests': 'Clients',
    'bookings.totalPrice': 'Prix total (MAD)',
    'bookings.source': 'Source',
    'bookings.notes': 'Notes',
    'bookings.notesPlaceholder': 'Remarques...',
    'bookings.nextStep': 'Suivant: Info client →',
    'bookings.guestName': 'Nom complet du client *',
    'bookings.guestNamePlaceholder': 'Mohammed Alami',
    'bookings.phone': 'Téléphone',
    'bookings.email': 'Email',
    'bookings.nationality': 'Nationalité',
    'bookings.nationalityDefault': 'Marocaine',
    'bookings.idType': "Type d'ID *",
    'bookings.idNumber': "N° d'ID *",
    'bookings.back': '← Retour',
    'bookings.create': 'Créer la réservation',
    'bookings.editPrice': 'Modifier le prix',
    'bookings.setPrice': 'Définir le prix',
    'bookings.guestLabel': 'Client:',
    'bookings.totalPriceLabel': 'Prix total (MAD) *',
    'bookings.currentPrice': 'Prix actuel:',
    'bookings.confirmPayment': 'Confirmer et enregistrer',
    'bookings.updatePrice': 'Mettre à jour',
    'bookings.fillRequired': 'Remplir les champs requis',
    'bookings.cin': 'CIN',
    'bookings.passport': 'Passeport',
    'bookings.residence': 'Carte de résidence',
    'guests.title': 'Clients',
    'guests.count': (n) => `${n} clients au total`,
    'guests.searchPlaceholder': "Rechercher par nom ou n° d'ID...",
    'guests.noGuests': 'Aucun client trouvé',
    'guests.noGuestsSearch': 'Essayez un autre nom ou numéro.',
    'guests.noGuestsEmpty': 'Les clients apparaîtront une fois les réservations créées.',
    'income.title': 'Revenus',
    'income.subtitle': 'Vue d\'ensemble des revenus sur toutes les propriétés',
    'income.thisMonth': 'Ce mois',
    'income.allTime': 'Total',
    'income.bestMonth': 'Meilleur mois',
    'income.chartTitle': 'Revenus mensuels — 12 derniers mois',
    'income.noData': 'Aucune donnée de paiement. Les revenus apparaîtront une fois les réservations payées.',
    'income.byProperty': (month) => `Par propriété — ${month}`,
    'income.noProperties': 'Aucune propriété trouvée',
    'income.noPropertiesDesc': 'Ajoutez une propriété pour commencer à suivre les revenus.',
    'login.title': 'Bienvenue',
    'login.subtitle': 'Connectez-vous à votre compte Nuzlio',
    'login.email': 'Adresse email',
    'login.password': 'Mot de passe',
    'login.submit': 'Se connecter',
    'login.noAccount': 'Pas de compte?',
    'login.createOne': 'Créez-en un',
    'register.title': 'Commencer à gérer',
    'register.subtitle': 'Créez votre compte Nuzlio gratuit',
    'register.fullName': 'Nom complet',
    'register.fullNamePlaceholder': 'Votre nom complet',
    'register.email': 'Email',
    'register.phone': 'Téléphone (optionnel)',
    'register.password': 'Mot de passe',
    'register.passwordPlaceholder': 'Min. 8 caractères',
    'register.submit': 'Créer un compte',
    'register.haveAccount': 'Déjà un compte?',
    'register.signIn': 'Se connecter',
    'bookingForm.badge': 'Demande de réservation',
    'bookingForm.step1': 'Détails du séjour',
    'bookingForm.step2': 'Info client',
    'bookingForm.checkIn': 'Arrivée *',
    'bookingForm.checkOut': 'Départ *',
    'bookingForm.numGuests': 'Nombre de clients *',
    'bookingForm.notes': 'Notes / Demandes spéciales',
    'bookingForm.notesPlaceholder': "Heure d'arrivée, besoins spéciaux...",
    'bookingForm.night': 'nuit',
    'bookingForm.nights': 'nuits',
    'bookingForm.continue': 'Continuer vers Info client →',
    'bookingForm.fullName': 'Nom complet *',
    'bookingForm.fullNamePlaceholder': 'Mohammed Alami',
    'bookingForm.phone': 'Téléphone',
    'bookingForm.email': 'Email',
    'bookingForm.nationality': 'Nationalité',
    'bookingForm.nationalityDefault': 'Marocaine',
    'bookingForm.idType': "Type d'ID *",
    'bookingForm.idNumber': "N° d'ID *",
    'bookingForm.idNumberPlaceholder': 'AB123456',
    'bookingForm.cin': 'CIN',
    'bookingForm.passport': 'Passeport',
    'bookingForm.residence': 'Carte de résidence',
    'bookingForm.summaryTitle': 'Résumé de la réservation',
    'bookingForm.summaryProperty': 'Propriété',
    'bookingForm.summaryCheckin': 'Arrivée',
    'bookingForm.summaryCheckout': 'Départ',
    'bookingForm.summaryDuration': 'Durée',
    'bookingForm.summaryTotal': 'Total',
    'bookingForm.back': '← Retour',
    'bookingForm.submit': 'Envoyer la demande',
    'bookingForm.errorDates': "Veuillez sélectionner les dates d'arrivée et de départ",
    'bookingForm.errorCheckout': "Le départ doit être après l'arrivée",
    'bookingForm.linkTitle': 'Lien introuvable',
    'bookingForm.linkDesc': 'Ce lien de réservation est invalide ou a expiré.',
    'bookingForm.doneTitle': 'Réservation soumise!',
    'bookingForm.doneDesc': (name) => `Votre demande pour ${name} a été reçue. L'hôte confirmera bientôt.`,
    'bookingForm.upToGuests': (n) => `Jusqu'à ${n} clients`,
    'bookingForm.footer': 'Propulsé par Nuzlio · Vos données sont sécurisées',
    // Pricing
    'pricing.title': 'Tarification simple et transparente',
    'pricing.subtitle': 'Commencez gratuitement. Évoluez à votre rythme. Sans frais cachés, sans surprises.',
    'pricing.plan.free': 'Gratuit',
    'pricing.plan.basic': 'Basique',
    'pricing.plan.pro': 'Pro',
    'pricing.plan.agency': 'Agence',
    'pricing.mostPopular': 'Le plus populaire',
    'pricing.feature.1property': '1 propriété',
    'pricing.feature.upTo3': "Jusqu'à 3 propriétés",
    'pricing.feature.upTo10': "Jusqu'à 10 propriétés",
    'pricing.feature.unlimitedProperties': 'Propriétés illimitées',
    'pricing.feature.unlimitedBookings': 'Réservations illimitées',
    'pricing.feature.guestRecords': 'Fiches clients',
    'pricing.feature.incomeReports': 'Rapports de revenus',
    'pricing.feature.publicLinks': 'Liens de réservation publics',
    'pricing.feature.prioritySupport': 'Support prioritaire',
    'pricing.btn.getStartedFree': 'Commencer gratuitement',
    'pricing.btn.start': (name) => `Démarrer ${name}`,
    'pricing.btn.currentPlan': 'Plan actuel',
    'pricing.btn.backToDashboard': '← Retour au tableau de bord',
  },

  ar: {
    'sidebar.dashboard': 'لوحة التحكم',
    'sidebar.properties': 'العقارات',
    'sidebar.bookings': 'الحجوزات',
    'sidebar.guests': 'الضيوف',
    'sidebar.income': 'الإيرادات',
    'sidebar.logout': 'تسجيل الخروج',
    'sidebar.subtitle': 'مدير العقارات',
    'sources.direct': 'مباشر',
    'sources.manual': 'يدوي',
    'sources.airbnb': 'Airbnb',
    'sources.booking': 'Booking.com',
    'sources.whatsapp_link': 'رابط واتساب',
    'sources.whatsapp': 'واتساب',
    'status.all': 'الكل',
    'status.confirmed': 'مؤكد',
    'status.cancelled': 'ملغي',
    'status.completed': 'مكتمل',
    'dashboard.greeting.morning': 'صباح الخير',
    'dashboard.greeting.afternoon': 'مساء الخير',
    'dashboard.greeting.evening': 'مساء النور',
    'dashboard.subtitle': 'إليك ما يحدث في عقاراتك اليوم.',
    'dashboard.stat.properties': 'إجمالي العقارات',
    'dashboard.stat.guests': 'الضيوف الحاليون',
    'dashboard.stat.checkouts': 'مغادرات اليوم',
    'dashboard.stat.income': 'إيرادات هذا الشهر',
    'dashboard.checkoutsToday': 'مغادرات اليوم',
    'dashboard.checkoutsTomorrow': 'مغادرات الغد',
    'dashboard.noCheckoutsToday': 'لا مغادرات اليوم 🎉',
    'dashboard.noCheckoutsTomorrow': 'لا مغادرات الغد',
    'dashboard.recentBookings': 'الحجوزات الأخيرة',
    'dashboard.viewAll': 'عرض الكل',
    'dashboard.noBookings': 'لا توجد حجوزات بعد',
    'dashboard.noBookingsDesc': 'أضف عقارك الأول وابدأ في قبول الحجوزات',
    'dashboard.today': 'اليوم',
    'dashboard.tomorrow': 'الغد',
    'table.guest': 'الضيف',
    'table.property': 'العقار',
    'table.checkIn': 'الوصول',
    'table.checkOut': 'المغادرة',
    'table.source': 'المصدر',
    'table.status': 'الحالة',
    'table.guests': 'الضيوف',
    'table.amount': 'المبلغ',
    'table.fullName': 'الاسم الكامل',
    'table.nationality': 'الجنسية',
    'table.idType': 'نوع الهوية',
    'table.idNumber': 'رقم الهوية',
    'table.phone': 'الهاتف',
    'table.email': 'البريد الإلكتروني',
    'table.bookings': 'الحجوزات',
    'table.city': 'المدينة',
    'table.bookingsThisMonth': 'الحجوزات هذا الشهر',
    'table.thisMonth': 'هذا الشهر',
    'table.allTime': 'الإجمالي الكلي',
    'action.cancel': 'إلغاء',
    'action.save': 'حفظ التغييرات',
    'action.addProperty': 'إضافة عقار',
    'action.newBooking': 'حجز جديد',
    'properties.title': 'العقارات',
    'properties.count': (n) => `${n} ${n === 1 ? 'عقار مُدار' : 'عقارات مُدارة'}`,
    'properties.addNew': 'إضافة عقار جديد',
    'properties.edit': 'تعديل العقار',
    'properties.empty': 'لا توجد عقارات بعد',
    'properties.emptyDesc': 'أضف عقارك الأول لبدء إدارة الحجوزات.',
    'properties.addFirst': 'إضافة أول عقار',
    'properties.name': 'اسم العقار *',
    'properties.namePlaceholder': 'شقة وسط المدينة',
    'properties.city': 'المدينة *',
    'properties.cityPlaceholder': 'الدار البيضاء',
    'properties.type': 'النوع',
    'properties.address': 'العنوان *',
    'properties.addressPlaceholder': 'شارع محمد الخامس 123',
    'properties.bedrooms': 'غرف النوم',
    'properties.maxGuests': 'الحد الأقصى للضيوف',
    'properties.priceNight': 'السعر/الليلة (د.م)',
    'properties.thisMonth': 'هذا الشهر',
    'properties.activeGuests': 'الضيوف النشطون',
    'properties.max': 'حد أقصى',
    'properties.confirmRemove': 'هل تريد حذف هذا العقار؟',
    'properties.type.apartment': 'شقة',
    'properties.type.villa': 'فيلا',
    'properties.type.riad': 'رياض',
    'properties.type.studio': 'استوديو',
    'properties.type.house': 'منزل',
    'bookings.title': 'الحجوزات',
    'bookings.count': (n) => `${n} حجز إجمالاً`,
    'bookings.searchPlaceholder': 'البحث عن ضيف أو عقار...',
    'bookings.noBookings': 'لا توجد حجوزات',
    'bookings.noBookingsDesc': 'أضف أول حجز أو عدّل الفلاتر',
    'bookings.modalTitle': 'حجز جديد',
    'bookings.step1': 'تفاصيل الحجز',
    'bookings.step2': 'معلومات الضيف',
    'bookings.property': 'العقار *',
    'bookings.selectProperty': 'اختر عقاراً',
    'bookings.checkIn': 'الوصول *',
    'bookings.checkOut': 'المغادرة *',
    'bookings.numGuests': 'الضيوف',
    'bookings.totalPrice': 'السعر الإجمالي (د.م)',
    'bookings.source': 'المصدر',
    'bookings.notes': 'ملاحظات',
    'bookings.notesPlaceholder': 'أي ملاحظات...',
    'bookings.nextStep': 'التالي: معلومات الضيف ←',
    'bookings.guestName': 'الاسم الكامل للضيف *',
    'bookings.guestNamePlaceholder': 'محمد العلمي',
    'bookings.phone': 'الهاتف',
    'bookings.email': 'البريد الإلكتروني',
    'bookings.nationality': 'الجنسية',
    'bookings.nationalityDefault': 'مغربية',
    'bookings.idType': 'نوع الهوية *',
    'bookings.idNumber': 'رقم الهوية *',
    'bookings.back': 'رجوع →',
    'bookings.create': 'إنشاء الحجز',
    'bookings.editPrice': 'تعديل السعر',
    'bookings.setPrice': 'تحديد السعر',
    'bookings.guestLabel': 'الضيف:',
    'bookings.totalPriceLabel': 'السعر الإجمالي (د.م) *',
    'bookings.currentPrice': 'السعر الحالي:',
    'bookings.confirmPayment': 'تأكيد وتسجيل الدفع',
    'bookings.updatePrice': 'تحديث السعر',
    'bookings.fillRequired': 'يرجى ملء الحقول المطلوبة',
    'bookings.cin': 'بطاقة التعرف',
    'bookings.passport': 'جواز السفر',
    'bookings.residence': 'بطاقة الإقامة',
    'guests.title': 'الضيوف',
    'guests.count': (n) => `${n} ضيف إجمالاً`,
    'guests.searchPlaceholder': 'البحث بالاسم أو رقم الهوية...',
    'guests.noGuests': 'لا يوجد ضيوف',
    'guests.noGuestsSearch': 'جرب اسماً أو رقماً مختلفاً.',
    'guests.noGuestsEmpty': 'سيظهر الضيوف هنا بمجرد إنشاء الحجوزات.',
    'income.title': 'الإيرادات',
    'income.subtitle': 'نظرة عامة على الإيرادات عبر جميع العقارات',
    'income.thisMonth': 'هذا الشهر',
    'income.allTime': 'الإجمالي الكلي',
    'income.bestMonth': 'أفضل شهر',
    'income.chartTitle': 'الإيرادات الشهرية — آخر 12 شهراً',
    'income.noData': 'لا توجد بيانات دفع بعد. ستظهر الإيرادات هنا بمجرد دفع الحجوزات.',
    'income.byProperty': (month) => `حسب العقار — ${month}`,
    'income.noProperties': 'لا توجد عقارات',
    'income.noPropertiesDesc': 'أضف عقاراً لبدء تتبع الإيرادات.',
    'login.title': 'مرحباً بعودتك',
    'login.subtitle': 'سجّل دخولك إلى حساب Nuzlio',
    'login.email': 'البريد الإلكتروني',
    'login.password': 'كلمة المرور',
    'login.submit': 'تسجيل الدخول',
    'login.noAccount': 'ليس لديك حساب؟',
    'login.createOne': 'أنشئ واحداً',
    'register.title': 'ابدأ الإدارة',
    'register.subtitle': 'أنشئ حسابك المجاني في Nuzlio',
    'register.fullName': 'الاسم الكامل',
    'register.fullNamePlaceholder': 'اسمك الكامل',
    'register.email': 'البريد الإلكتروني',
    'register.phone': 'الهاتف (اختياري)',
    'register.password': 'كلمة المرور',
    'register.passwordPlaceholder': '8 أحرف على الأقل',
    'register.submit': 'إنشاء حساب',
    'register.haveAccount': 'لديك حساب بالفعل؟',
    'register.signIn': 'سجّل دخولك',
    'bookingForm.badge': 'طلب حجز',
    'bookingForm.step1': 'تفاصيل الإقامة',
    'bookingForm.step2': 'معلومات الضيف',
    'bookingForm.checkIn': 'الوصول *',
    'bookingForm.checkOut': 'المغادرة *',
    'bookingForm.numGuests': 'عدد الضيوف *',
    'bookingForm.notes': 'ملاحظات / طلبات خاصة',
    'bookingForm.notesPlaceholder': 'وقت الوصول، متطلبات خاصة...',
    'bookingForm.night': 'ليلة',
    'bookingForm.nights': 'ليالي',
    'bookingForm.continue': '← المتابعة إلى معلومات الضيف',
    'bookingForm.fullName': 'الاسم الكامل *',
    'bookingForm.fullNamePlaceholder': 'محمد العلمي',
    'bookingForm.phone': 'الهاتف',
    'bookingForm.email': 'البريد الإلكتروني',
    'bookingForm.nationality': 'الجنسية',
    'bookingForm.nationalityDefault': 'مغربية',
    'bookingForm.idType': 'نوع الهوية *',
    'bookingForm.idNumber': 'رقم الهوية *',
    'bookingForm.idNumberPlaceholder': 'AB123456',
    'bookingForm.cin': 'بطاقة التعرف',
    'bookingForm.passport': 'جواز السفر',
    'bookingForm.residence': 'بطاقة الإقامة',
    'bookingForm.summaryTitle': 'ملخص الحجز',
    'bookingForm.summaryProperty': 'العقار',
    'bookingForm.summaryCheckin': 'الوصول',
    'bookingForm.summaryCheckout': 'المغادرة',
    'bookingForm.summaryDuration': 'المدة',
    'bookingForm.summaryTotal': 'الإجمالي',
    'bookingForm.back': 'رجوع →',
    'bookingForm.submit': 'إرسال طلب الحجز',
    'bookingForm.errorDates': 'يرجى تحديد تواريخ الوصول والمغادرة',
    'bookingForm.errorCheckout': 'يجب أن تكون المغادرة بعد الوصول',
    'bookingForm.linkTitle': 'الرابط غير موجود',
    'bookingForm.linkDesc': 'رابط الحجز هذا غير صالح أو منتهي الصلاحية.',
    'bookingForm.doneTitle': 'تم تقديم الحجز!',
    'bookingForm.doneDesc': (name) => `تم استلام طلبك لـ ${name}. سيقوم المضيف بالتأكيد قريباً.`,
    'bookingForm.upToGuests': (n) => `حتى ${n} ضيوف`,
    'bookingForm.footer': 'مدعوم بـ Nuzlio · بياناتك محمية بأمان',
    // Pricing
    'pricing.title': 'أسعار بسيطة وشفافة',
    'pricing.subtitle': 'ابدأ مجاناً. توسّع مع نموك. بدون رسوم خفية، بدون مفاجآت.',
    'pricing.plan.free': 'مجاني',
    'pricing.plan.basic': 'أساسي',
    'pricing.plan.pro': 'محترف',
    'pricing.plan.agency': 'وكالة',
    'pricing.mostPopular': 'الأكثر شعبية',
    'pricing.feature.1property': 'عقار واحد',
    'pricing.feature.upTo3': 'حتى 3 عقارات',
    'pricing.feature.upTo10': 'حتى 10 عقارات',
    'pricing.feature.unlimitedProperties': 'عقارات غير محدودة',
    'pricing.feature.unlimitedBookings': 'حجوزات غير محدودة',
    'pricing.feature.guestRecords': 'سجلات الضيوف',
    'pricing.feature.incomeReports': 'تقارير الإيرادات',
    'pricing.feature.publicLinks': 'روابط الحجز العامة',
    'pricing.feature.prioritySupport': 'دعم ذو أولوية',
    'pricing.btn.getStartedFree': 'ابدأ مجاناً',
    'pricing.btn.start': (name) => `ابدأ ${name}`,
    'pricing.btn.currentPlan': 'الخطة الحالية',
    'pricing.btn.backToDashboard': 'العودة إلى لوحة التحكم →',
  },
};

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem('nuzlio_lang') || 'en');

  const setLang = (l) => {
    localStorage.setItem('nuzlio_lang', l);
    setLangState(l);
  };

  useEffect(() => {
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  }, [lang]);

  const t = (key, arg) => {
    const val = translations[lang]?.[key] ?? translations.en?.[key] ?? key;
    return typeof val === 'function' ? val(arg) : val;
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, isRtl: lang === 'ar' }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => useContext(LanguageContext);
